/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import React, { useState, useRef, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  IconButton,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Tooltip,
} from "@material-ui/core"
import { Add, Edit, Publish, Face } from "@material-ui/icons"
import { useSnackbar } from "notistack"
import axiosInstance from "src/utils/axios"
import useUsers from "src/hooks/useUsers"
import { compressImage, withCacheBust } from "../imageUtils"
import { processCompleteZip } from "../zipUtils"
import {
  createFaceImportTemplate,
  generateErrorReport,
} from "../RostrosMasivos/createExcel"
import ImportMasivoDialog from "./ImportMasivoDialog"
import ImportProgressMonitor from "./ImportProgressMonitor"
import InformativeDialog from "../Dialogs/InformativeDialog"
import NewAndEditRostroDialog from "./NewAndEditRostroDialog"
import ImageCropDialog from "../Dialogs/ImageCropDialog"

const LazyImage = ({ rostro, zipImages: zi }) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (rostro.imagen && !loaded && zi.size > 0) {
      const key = rostro.imagen.toLowerCase().replace(/\.[^/.]+$/, "")
      const f = zi.get(key) || zi.get(rostro.imagen.toLowerCase())
      if (f) {
        setImageUrl(URL.createObjectURL(f))
        setLoaded(true)
      } else setLoaded(true)
    }
  }, [rostro.imagen, zi, loaded])
  return (
    <Box
      style={{
        width: 60,
        height: 60,
        border: "1px solid #ccc",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Rostro"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Face style={{ fontSize: 24, color: "#ccc" }} />
      )}
    </Box>
  )
}

const rostroImageSrc = rostro =>
  withCacheBust(
    rostro?.imagen_url || rostro?.imagenUrl || "",
    rostro?.fecha_actualizacion
  )

const NewAndEditRostro = ({
  conjunto,
  selectedRostro = null,
  onRostroCreated,
  onRostroUpdated,
  devices = [],
  selectedDevice = null,
  disabled = false,
  setAllUniqueFaces,
  allDevicesOriginal = null,
  moduloBiometricosResidentes,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { usersByUnit, optionsById } = useUsers()
  const fileInputRef = useRef(null)
  const importFileInputRef = useRef(null)
  const zipFileInputRef = useRef(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [showInfoDialog, setShowInfoDialog] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)

  const [imageFile, setImageFile] = useState(null)
  const [imageChanged, setImageChanged] = useState(false)
  const [imageCompressing, setImageCompressing] = useState(false)
  const [compressionInfo, setCompressionInfo] = useState(null)
  const [imagePreview, setImagePreview] = useState(() =>
    rostroImageSrc(selectedRostro)
  )

  // ── Biométricos ──────────────────────────────────────────────────────────────
  const [biometricDevices, setBiometricDevices] = useState([])

  const [pinError, setPinError] = useState("")
  const [showPin, setShowPin] = useState(false)

  const [formData, setFormData] = useState(() => ({
    nombre: selectedRostro?.nombre || "",
    identificacion: selectedRostro?.identificacion || "",
    pisos: selectedRostro?.pisos || [],
    destino: selectedRostro?.destino || "",
    tipo: selectedRostro?.tipo || "propietario",
    estado: selectedRostro?.estado || "activo",
    observaciones: selectedRostro?.observaciones || "",
    dispositivos_biometricos: [],
    pin: selectedRostro?.pin ?? "",
    tarjeta: selectedRostro?.tarjeta ?? "",
  }))

  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importStep, setImportStep] = useState(0)
  const [importFile, setImportFile] = useState(null)
  const [importData, setImportData] = useState(null)
  const [importLoading, setImportLoading] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [importResults, setImportResults] = useState({
    successful: [],
    failed: [],
    conflicted: [],
    summary: null,
  })
  const [importJobId, setImportJobId] = useState(null)
  const [showProgressMonitor, setShowProgressMonitor] = useState(false)
  const [selectedBiometricDevices, setSelectedBiometricDevices] = useState([])

  const [zipFile, setZipFile] = useState(null)
  const [zipProcessing, setZipProcessing] = useState(false)
  const [zipImages, setZipImages] = useState(new Map())
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize] = useState(20)

  const isEditing = Boolean(selectedRostro)

  const FLOOR_PIN_MAPPING = Object.fromEntries([
    ...Array.from({ length: 9 }, (_, i) => [-9 + i, -9 + i]),
    ...Array.from({ length: 35 }, (_, i) => [i + 1, i + 1]),
  ])
  const ALL_FLOORS_VALUE = 999
  const generateFloorOptions = (maxFloor = 35, maxSubsoil = 9) => {
    const sub = Array.from({ length: maxSubsoil }, (_, i) => ({
      label: `Subsuelo ${maxSubsoil - i}`,
      value: -(maxSubsoil - i),
      pin: FLOOR_PIN_MAPPING[`-${maxSubsoil - i}`],
    }))
    const fl = Array.from({ length: maxFloor }, (_, i) => ({
      label: `Piso ${i + 1}`,
      value: i + 1,
      pin: FLOOR_PIN_MAPPING[i + 1] || null,
    }))
    return [...sub.reverse(), ...fl]
  }
  const FLOOR_OPTIONS = generateFloorOptions()

  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [imageToCrop, setImageToCrop] = useState(null)

  const getFloorLabel = v => {
    if (v === ALL_FLOORS_VALUE) return "Todos los Pisos"
    return v < 0 ? `Subsuelo ${Math.abs(v)}` : `Piso ${v}`
  }

  const handlePisosChange = event => {
    const { value } = event.target
    let newPisos =
      typeof value === "string" ? value.split(",").map(Number) : value
    if (newPisos.includes(ALL_FLOORS_VALUE)) {
      newPisos =
        formData.pisos.length === FLOOR_OPTIONS.length
          ? []
          : FLOOR_OPTIONS.map(o => o.value)
    }
    setFormData({ ...formData, pisos: newPisos })
  }

  const parseTarjetas = raw => {
    if (!raw) return []

    if (Array.isArray(raw)) {
      return raw.filter(
        t => t && typeof t === "object" && t.tipo && t.codigo !== undefined
      )
    }

    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
      } catch {
        // JSON inválido
      }
    }

    return []
  }

  const [tarjetas, setTarjetas] = useState(() =>
    parseTarjetas(selectedRostro?.tarjeta || "[]")
  )

  useEffect(() => {
    setBiometricDevices(
      devices?.length
        ? devices.map(d => ({
            id: d.rostroId,
            nombre: d.nombre,
            ubicacion: d.ubicacion,
            estado: d.estado || "activo",
            ip: d.ip,
            marca: d.marca,
          }))
        : []
    )
  }, [devices])

  const mapDeviceIdsToRostroIds = ids =>
    ids.map(id => {
      const d = biometricDevices.find(x => x.id === id)
      return d ? d.id : id
    })
  const getDeviceNameById = id =>
    biometricDevices.find(d => d.id === id)?.nombre || id
  const getDeviceNamesFromIds = ids => (ids || []).map(getDeviceNameById)

  const refreshDevicesRealTime = useCallback(async () => {
    if (!conjunto?.id) return
    try {
      const { data } = await axiosInstance.get(
        `api/biometrics/conjuntos/${conjunto.id}/biometricos/status`
      )
      if (data.success && data.data.devices_status) {
        setBiometricDevices(
          data.data.devices_status.map(d => ({
            id: d.rostro_id,
            nombre: d.device_name,
            ubicacion: d.ubicacion,
            estado: d.status,
            ip: d.ip,
            marca: d.marca,
          }))
        )
      }
    } catch {
      enqueueSnackbar("Error al obtener estados de dispositivos", {
        variant: "error",
      })
    }
  }, [conjunto?.id, enqueueSnackbar])

  const handleOpen = () => {
    if (localStorage.getItem("hideFaceInstructions") === "true")
      handleOpenForm()
    else setShowInfoDialog(true)
  }

  const handleOpenForm = () => {
    setShowInfoDialog(false)
    setDialogOpen(true)
    setActiveTab(0)

    if (isEditing && selectedRostro) {
      const originalDevices = allDevicesOriginal || selectedRostro.devices || []
      const deviceIds = originalDevices.map(d => d.deviceId)
      let destinoValue = selectedRostro.destino || ""
      if (destinoValue && !destinoValue.includes("/")) {
        const match = usersByUnit.find(u => u.Casa === destinoValue)
        if (match) destinoValue = match.id
      }
      setFormData({
        nombre: selectedRostro.nombre || "",
        identificacion: selectedRostro.identificacion || "",
        destino: destinoValue,
        pisos: selectedRostro.pisos || [],
        tipo: selectedRostro.tipo || "",
        estado: selectedRostro.estado || "",
        observaciones: selectedRostro.observaciones || "",
        dispositivos_biometricos: deviceIds,
        pin: selectedRostro.pin ?? "",
        tarjeta: selectedRostro.tarjeta ?? "",
      })
      setPinError("")
      if (selectedRostro.imagen_url || selectedRostro.imagenUrl)
        setImagePreview(rostroImageSrc(selectedRostro))
    } else {
      setFormData({
        nombre: "",
        identificacion: "",
        destino: "",
        tipo: "",
        pisos: [],
        estado: "activo",
        observaciones: "",
        dispositivos_biometricos: [],
        pin: "",
        tarjeta: "",
      })
      setImagePreview(null)
      setPinError("")
      setTarjetas([])
    }
  }

  const handleClose = () => {
    setDialogOpen(false)
    setActiveTab(0)
    setImageFile(null)
    setImageChanged(false)
    setCompressionInfo(null)
    setPinError("")
    setShowPin(false)
    if (!isEditing) setTarjetas([])
    setImagePreview(rostroImageSrc(selectedRostro))
    setFormData({
      nombre: selectedRostro?.nombre || "",
      identificacion: selectedRostro?.identificacion || "",
      destino: selectedRostro?.destino || "",
      tipo: selectedRostro?.tipo || "propietario",
      estado: selectedRostro?.estado || "activo",
      pisos: selectedRostro?.pisos || [],
      observaciones: selectedRostro?.observaciones || "",
      dispositivos_biometricos: selectedRostro?.dispositivos_biometricos || [],
      pin: "",
      tarjeta: "",
    })
    setLoading(false)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleInputChange = field => event => {
    let { value } = event.target
    if (field === "identificacion") {
      value = value
        .replace(/[^0-9A-Za-z]/g, "")
        .toUpperCase()
        .substring(0, 15)
    }
    setFormData({ ...formData, [field]: value })
  }

  const getHelperText = () => {
    if (isEditing)
      return "La identificación no se puede modificar en modo edición"
    if (!formData.identificacion.trim() && formData.identificacion !== "")
      return "La identificación es requerida"
    return ""
  }

  const handleImageClick = () => fileInputRef.current?.click()

  const handleImageChange = async event => {
    const file = event.target.files[0]
    if (!file) return
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Solo se permiten archivos de imagen", {
        variant: "error",
      })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("La imagen es demasiado grande (máximo 5MB)", {
        variant: "error",
      })
      return
    }
    const reader = new FileReader()
    reader.onload = e => {
      setImageToCrop(e.target.result)
      setCropDialogOpen(true)
    }
    reader.readAsDataURL(file)
  }

  const handleCropComplete = async croppedFile => {
    setImageCompressing(true)
    try {
      const {
        file: cf,
        originalSize,
        compressedSize,
        compressionRatio,
        dimensions,
      } = await compressImage(croppedFile, {
        maxWidth: 1024,
        maxHeight: 768,
        quality: 0.8,
        maxSizeKB: 500,
      })

      setCompressionInfo({
        originalSize: Math.round(originalSize / 1024),
        compressedSize: Math.round(compressedSize / 1024),
        compressionRatio,
        dimensions,
      })
      setImageFile(cf)
      setImageChanged(true)

      const reader = new FileReader()
      reader.onload = e => setImagePreview(e.target.result)
      reader.readAsDataURL(cf)

      enqueueSnackbar(
        compressionRatio > 0
          ? `Imagen recortada y comprimida (${compressionRatio}% reducción)`
          : "Imagen recortada exitosamente",
        { variant: "success" }
      )
    } catch (err) {
      enqueueSnackbar(`Error al procesar la imagen: ${err.message}`, {
        variant: "error",
      })
    } finally {
      setImageCompressing(false)
    }
  }

  // PIN
  const handlePinChange = event => {
    const raw = (event.target.value ?? "").replace(/\D/g, "").substring(0, 8)
    setFormData({ ...formData, pin: raw })
    if (raw.length === 0) setPinError("")
    else if (raw.length < 4) setPinError("El PIN debe tener al menos 4 dígitos")
    else setPinError("")
  }

  const getButtonText = () => {
    if (loading) return isEditing ? "Actualizando..." : "Guardando..."
    if (isEditing)
      return imageChanged
        ? "Actualizar con Nueva Imagen"
        : "Actualizar Información"
    return "Crear Rostro"
  }

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      enqueueSnackbar("El nombre es requerido", { variant: "error" })
      setActiveTab(0)
      return
    }
    if (!formData.identificacion.trim()) {
      enqueueSnackbar("La cédula/pasaporte es requerida", { variant: "error" })
      setActiveTab(0)
      return
    }
    const tieneAccesoBiometrico =
      formData.pin?.length >= 4 || tarjetas.length > 0
    if (!isEditing && !imageFile && !tieneAccesoBiometrico) {
      enqueueSnackbar(
        "Debe cargar una imagen del rostro, o agregar al menos un PIN, TAG o QR",
        { variant: "error" }
      )
      setActiveTab(0)
      return
    }
    if (formData.dispositivos_biometricos.length === 0) {
      enqueueSnackbar("Debe seleccionar al menos un dispositivo biométrico", {
        variant: "error",
      })
      setActiveTab(0)
      return
    }
    const pin = formData.pin ?? ""
    if (pin.length > 0 && (pin.length < 4 || pin.length > 8)) {
      enqueueSnackbar("El PIN debe tener entre 4 y 8 dígitos", {
        variant: "error",
      })
      setPinError("El PIN debe tener entre 4 y 8 dígitos")
      setActiveTab(1)
      return
    }
    setLoading(true)
    try {
      if (isEditing) await updateRostro()
      else await createRostro()
    } catch {
      enqueueSnackbar("Error inesperado al procesar la solicitud", {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const buildFormDataToSend = () => {
    const fd = new FormData()
    const selectedUnit = usersByUnit.find(u => u.id === formData.destino)
    const destinoValue = selectedUnit ? selectedUnit.Casa : formData.destino
    fd.append("nombre", formData.nombre.trim())
    fd.append("identificacion", formData.identificacion.trim())
    fd.append("destino", destinoValue)
    fd.append("tipo", formData.tipo)
    fd.append("estado", formData.estado)
    fd.append("pisos", JSON.stringify(formData.pisos))
    fd.append("observaciones", formData.observaciones)
    fd.append("pin", formData.pin || "")
    fd.append("tarjeta", JSON.stringify(tarjetas))
    return fd
  }

  const createRostro = async () => {
    try {
      const fd = buildFormDataToSend()
      fd.append(
        "dispositivos_biometricos",
        JSON.stringify(
          mapDeviceIdsToRostroIds(formData.dispositivos_biometricos)
        )
      )
      if (imageFile) fd.append("imagen", imageFile)

      const response = await axiosInstance.post(
        `/api/biometrics/conjuntos/${conjunto.id}/rostros/`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      if (response.status === 201 && response.data.success) {
        enqueueSnackbar("Rostro creado exitosamente", { variant: "success" })
        const newRostro = {
          id: response.data.rostro?.id || Date.now().toString(),
          ...formData,
          tarjeta: tarjetas,
          imagen_url: imageFile ? URL.createObjectURL(imageFile) : null,
          fecha_creacion: new Date(),
          devices: mapDeviceIdsToRostroIds(
            formData.dispositivos_biometricos
          ).map(dId => {
            const d = biometricDevices.find(x => x.id === dId)
            return {
              deviceId: dId,
              deviceName: d?.nombre || "",
              deviceUbicacion: d?.ubicacion || "",
              deviceIp: d?.ip || "",
              deviceMarca: d?.marca || "",
            }
          }),
        }
        newRostro.uniqueKey =
          newRostro.identificacion || newRostro.nombre || newRostro.id
        setAllUniqueFaces(prev => [...prev, newRostro])
        if (onRostroCreated) onRostroCreated(response.data)
        handleClose()
      } else
        throw new Error(response.data.message || "Error al crear el rostro")
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error inesperado"
      enqueueSnackbar(msg, { variant: "error" })
    }
  }

  const updateRostro = async () => {
    try {
      const fd = buildFormDataToSend()
      fd.append(
        "dispositivos_biometricos",
        JSON.stringify(formData.dispositivos_biometricos)
      )
      fd.append(
        "todos_dispositivos_conjunto",
        JSON.stringify(biometricDevices.map(d => d.id))
      )
      if (imageFile && imageChanged) fd.append("imagen", imageFile)

      const response = await axiosInstance.put(
        `/api/biometrics/conjuntos/${conjunto.id}/rostros/${selectedRostro.identificacion}/edit/`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      if (
        (response.status === 200 || response.status === 207) &&
        response.data.success
      ) {
        enqueueSnackbar("Rostro actualizado exitosamente", {
          variant: "success",
        })
        const { identificacion_original } = response.data.data
        const newDevices = formData.dispositivos_biometricos.map(dId => {
          const di = devices.find(d => d.rostroId === dId)
          return {
            deviceId: dId,
            deviceName: di?.nombre || "Desconocido",
            deviceUbicacion: di?.ubicacion || "",
            deviceIp: di?.ip || "",
            deviceMarca: di?.marca || "",
          }
        })
        setAllUniqueFaces(prev =>
          prev.map(face =>
            face.identificacion ===
              (identificacion_original || selectedRostro.identificacion) ||
            face.identificacion === formData.identificacion.trim()
              ? {
                  ...face,
                  ...formData,
                  tarjeta: tarjetas,
                  imagen_url:
                    imageChanged && imageFile
                      ? URL.createObjectURL(imageFile)
                      : face.imagen_url,
                  devices: newDevices,
                  fecha_actualizacion: new Date().toISOString(),
                }
              : face
          )
        )
        if (response.status === 207 && response.data.data?.alerts) {
          response.data.data.alerts.forEach(a =>
            enqueueSnackbar(a.message, {
              variant: a.type === "ssh_failed" ? "error" : "warning",
              persist: a.type === "device_offline",
            })
          )
        }
        if (onRostroUpdated) onRostroUpdated(response.data)
        handleClose()
      } else
        throw new Error(
          response.data.message || "Error al actualizar el rostro"
        )
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error inesperado"
      enqueueSnackbar(msg, { variant: "error" })
    }
  }

  const importSteps = [
    "Descargar Plantilla",
    "Subir Archivo",
    "Validar Datos",
    "Seleccionar Dispositivos",
    "Resultados",
  ]

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true)
    setImportStep(0)
    setImportFile(null)
    setImportData(null)
    setImportErrors([])
  }
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false)
    setImportStep(0)
    setImportFile(null)
    setImportData(null)
    setImportErrors([])
    setSelectedBiometricDevices([])
    if (importFileInputRef.current) importFileInputRef.current.value = ""
    setZipFile(null)
    setZipImages(new Map())
    setZipProcessing(false)
    if (zipFileInputRef.current) zipFileInputRef.current.value = ""
  }

  const handleDownloadTemplate = () => {
    try {
      createFaceImportTemplate(conjunto, usersByUnit, biometricDevices, {
        includeZipInstructions: true,
      })
      enqueueSnackbar("Plantilla descargada exitosamente", {
        variant: "success",
      })
      setImportStep(1)
    } catch (err) {
      enqueueSnackbar(`Error al generar la plantilla: ${err.message}`, {
        variant: "error",
      })
    }
  }

  const handleDownloadErrors = () => {
    if (importErrors?.length > 0) {
      generateErrorReport(importErrors, conjunto)
      enqueueSnackbar("Reporte de errores descargado", { variant: "info" })
    }
  }

  const handleZipFileChange = async event => {
    const file = event.target.files[0]
    if (!file || !file.name.match(/\.zip$/)) {
      enqueueSnackbar("Por favor seleccione un archivo ZIP válido", {
        variant: "error",
      })
      return
    }
    setZipProcessing(true)
    setZipFile(file)
    try {
      const JSZip = (await import("jszip")).default
      const zip = new JSZip()
      const zd = await zip.loadAsync(file)
      const imageMap = new Map()
      const promises = []
      const getMime = fn =>
        ({ jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" })[
          fn.split(".").pop().toLowerCase()
        ] || "image/jpeg"
      zd.forEach((path, entry) => {
        if (!entry.dir && /\.(jpg|jpeg|png)$/i.test(path)) {
          const fn = path.split("/").pop().toLowerCase()
          const key = fn.replace(/\.[^/.]+$/, "")
          promises.push(
            entry.async("blob").then(async blob => {
              const f = new File([blob], fn, {
                type: getMime(fn),
                lastModified: Date.now(),
              })
              try {
                const { file: cf } = await compressImage(f, {
                  maxWidth: 800,
                  maxHeight: 600,
                  quality: 0.7,
                  maxSizeKB: 100,
                })
                imageMap.set(key, cf)
              } catch {
                /* skip */
              }
            })
          )
        }
      })
      await Promise.all(promises)
      setZipImages(imageMap)
      enqueueSnackbar(
        imageMap.size === 0
          ? "Ninguna imagen pudo procesarse"
          : `${imageMap.size} imagen(es) procesadas`,
        { variant: imageMap.size === 0 ? "error" : "success" }
      )
    } catch (err) {
      enqueueSnackbar(`Error al procesar ZIP: ${err.message}`, {
        variant: "error",
      })
    } finally {
      setZipProcessing(false)
    }
  }

  const handleProcessImportFile = async () => {
    if (!zipFile) {
      enqueueSnackbar("Por favor seleccione un archivo ZIP", {
        variant: "error",
      })
      return
    }
    setImportLoading(true)
    try {
      const result = await processCompleteZip(zipFile)
      if (result.success) {
        setImportData(result)
        setImportErrors(result.errors || [])
        setImportStep(2)
        enqueueSnackbar(
          result.errors?.length > 0
            ? `ZIP procesado con ${result.errors.length} errores`
            : "ZIP procesado exitosamente",
          { variant: result.errors?.length > 0 ? "warning" : "success" }
        )
      } else throw new Error(result.error || "Error al procesar el ZIP")
    } catch (err) {
      enqueueSnackbar(`Error al procesar el ZIP: ${err.message}`, {
        variant: "error",
      })
    } finally {
      setImportLoading(false)
    }
  }

  const handleDevicesSelect = async () => {
    setImportLoading(true)
    try {
      await refreshDevicesRealTime()
      setImportStep(3)
      enqueueSnackbar("Dispositivos actualizados", { variant: "success" })
    } catch {
      enqueueSnackbar("Error al actualizar dispositivos", { variant: "error" })
    } finally {
      setImportLoading(false)
    }
  }

  const handleContinueWithDevices = () => {
    if (selectedBiometricDevices.length === 0) {
      enqueueSnackbar("Debe seleccionar al menos un dispositivo biométrico", {
        variant: "error",
      })
      return
    }
    setImportStep(3)
  }

  const handleBiometricDeviceSelectionChange = event => {
    const { value } = event.target
    setSelectedBiometricDevices(
      typeof value === "string" ? value.split(",") : value
    )
  }

  useEffect(() => {
    setCurrentPage(0)
  }, [importData])

  const handleMassImport = async () => {
    if (!importData?.data?.length) {
      enqueueSnackbar("No hay datos válidos para importar", {
        variant: "error",
      })
      return
    }
    if (selectedBiometricDevices.length === 0) {
      enqueueSnackbar("No hay dispositivos seleccionados", { variant: "error" })
      return
    }
    setImportLoading(true)
    const rostrosToImport = importData.data.map(r => {
      let img = null
      if (r.imagen) {
        const k = r.imagen.toLowerCase().replace(/\.[^/.]+$/, "")
        img = zipImages.get(k) || zipImages.get(r.imagen.toLowerCase())
      }
      return {
        nombre: r.nombre,
        identificacion: r.identificacion,
        destino: r.destino || "",
        tipo: r.tipo || "propietario",
        estado: r.estado || "activo",
        pisos: r.pisos || [],
        observaciones: r.observaciones || "",
        pin: r.pin || "",
        tarjeta: r.tarjeta || [],
        dispositivos_biometricos: mapDeviceIdsToRostroIds(
          selectedBiometricDevices
        ),
        imagen: img,
      }
    })
    try {
      const fd = new FormData()
      fd.append(
        "rostros",
        JSON.stringify(
          rostrosToImport.map(r => ({
            ...r,
            imagen: undefined,
            tarjeta: JSON.stringify(r.tarjeta), // ← serializar igual que el form individual
          }))
        )
      )
      rostrosToImport.forEach((r, i) => {
        if (r.imagen) fd.append(`imagen_${i}`, r.imagen)
      })
      const response = await axiosInstance.post(
        `/api/biometrics/conjuntos/${conjunto.id}/rostros/import/`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      if (response.status === 202) {
        setImportJobId(response.data.job_id)
        setShowProgressMonitor(true)
        setImportDialogOpen(false)
        enqueueSnackbar(
          `Importación iniciada. ${rostrosToImport.length} rostros en cola.`,
          { variant: "success" }
        )
        return
      }
      if (response.status === 201 || response.status === 207) {
        const {
          resumen_general: summary,
          importaciones_exitosas: ok,
          importaciones_fallidas: fail,
          reporte_conflictos: rc = {},
        } = response.data
        const successful = (ok || []).map(r => ({
          ...r,
          estado: "exitoso",
          dispositivos_nombres: getDeviceNamesFromIds(
            r.dispositivos_procesados || r.dispositivos_originales
          ),
          razon: "Creado exitosamente",
          tipo: "exitoso",
        }))
        const failed = (fail || []).map(r => ({
          ...r,
          estado: "fallido",
          dispositivos_nombres: getDeviceNamesFromIds(
            r.dispositivos_intentados || []
          ),
          razon: r.error || "Error en el procesamiento",
          tipo: "error",
        }))
        setImportResults({
          successful,
          failed,
          conflicted: [],
          summary,
          conflictsByDevice: rc.conflictos_por_dispositivo || {},
          deviceStats: rc.estadisticas_dispositivos || {},
        })
        if (summary) {
          const { importExitosas, totalRostrosEnviados } = summary
          enqueueSnackbar(
            importExitosas === totalRostrosEnviados
              ? `✅ ${importExitosas} rostros importados`
              : importExitosas > 0
                ? `⚠️ ${importExitosas}/${totalRostrosEnviados} rostros procesados`
                : "❌ Ningún rostro pudo procesarse",
            {
              variant:
                importExitosas === totalRostrosEnviados
                  ? "success"
                  : importExitosas > 0
                    ? "warning"
                    : "error",
            }
          )
        }
        setImportStep(4)
      }
    } catch (err) {
      enqueueSnackbar(
        `Error en importación: ${err.response?.data?.error || err.message}`,
        { variant: "error" }
      )
    } finally {
      setImportLoading(false)
    }
  }

  return (
    <>
      {!isEditing && !selectedDevice && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Publish />}
          onClick={handleOpenImportDialog}
          style={{ marginRight: 8 }}
          disabled={disabled}
        >
          Importar Masivo
        </Button>
      )}
      {isEditing ? (
        <Tooltip title="Editar Rostro">
          <IconButton
            color="primary"
            onClick={handleOpen}
            size="small"
            disabled={disabled}
          >
            <Edit style={{ color: "#213759" }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add style={{ color: "white" }} />}
          onClick={handleOpen}
          disabled={disabled}
        >
          Nuevo Rostro
        </Button>
      )}

      {importJobId && (
        <Box
          position="fixed"
          bottom={24}
          right={24}
          zIndex={9999}
          style={{ cursor: "pointer" }}
          onClick={() => setShowProgressMonitor(true)}
        >
          <Paper
            elevation={6}
            style={{
              padding: "12px 20px",
              backgroundColor: "#1976d2",
              color: "white",
              borderRadius: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <CircularProgress size={20} style={{ color: "white" }} />
            <Typography variant="body2" style={{ fontWeight: "bold" }}>
              Importación en progreso
            </Typography>
            <Chip
              size="small"
              label="Ver detalles"
              style={{
                backgroundColor: "white",
                color: "#1976d2",
                fontWeight: "bold",
              }}
            />
          </Paper>
        </Box>
      )}

      <NewAndEditRostroDialog
        open={dialogOpen}
        onClose={handleClose}
        isEditing={isEditing}
        loading={loading}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        getHelperText={getHelperText}
        getButtonText={getButtonText}
        handleSubmit={handleSubmit}
        imagePreview={imagePreview}
        imageChanged={imageChanged}
        imageCompressing={imageCompressing}
        compressionInfo={compressionInfo}
        fileInputRef={fileInputRef}
        handleImageClick={handleImageClick}
        handleImageChange={handleImageChange}
        biometricDevices={biometricDevices}
        usersByUnit={usersByUnit}
        optionsById={optionsById}
        FLOOR_OPTIONS={FLOOR_OPTIONS}
        ALL_FLOORS_VALUE={ALL_FLOORS_VALUE}
        getFloorLabel={getFloorLabel}
        handlePisosChange={handlePisosChange}
        pinError={pinError}
        setPinError={setPinError}
        showPin={showPin}
        setShowPin={setShowPin}
        handlePinChange={handlePinChange}
        tarjetas={tarjetas}
        setTarjetas={setTarjetas}
        moduloBiometricosResidentes={moduloBiometricosResidentes}
      />

      {/* ── Diálogo importación masiva ── */}
      {!selectedDevice && (
        <>
          <ImportMasivoDialog
            open={importDialogOpen}
            onClose={handleCloseImportDialog}
            importLoading={importLoading}
            importStep={importStep}
            setImportStep={setImportStep}
            importSteps={importSteps}
            handleDownloadTemplate={handleDownloadTemplate}
            zipFile={zipFile}
            zipProcessing={zipProcessing}
            zipImages={zipImages}
            handleZipFileChange={handleZipFileChange}
            handleProcessImportFile={handleProcessImportFile}
            importData={importData}
            importErrors={importErrors}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            LazyImage={LazyImage}
            handleDownloadErrors={handleDownloadErrors}
            handleDevicesSelect={handleDevicesSelect}
            biometricDevices={biometricDevices}
            selectedBiometricDevices={selectedBiometricDevices}
            handleBiometricDeviceSelectionChange={
              handleBiometricDeviceSelectionChange
            }
            handleMassImport={handleMassImport}
            importResults={importResults}
            getDeviceNameById={getDeviceNameById}
            handleContinueWithDevices={handleContinueWithDevices}
            importFile={importFile}
          />
          <ImportProgressMonitor
            open={showProgressMonitor}
            onClose={() => setShowProgressMonitor(false)}
            jobId={importJobId}
            conjuntoId={conjunto?.id}
          />
          <InformativeDialog
            open={showInfoDialog}
            onClose={() => setShowInfoDialog(false)}
            onContinue={handleOpenForm}
            isEditing={isEditing}
          />
          <ImageCropDialog
            open={cropDialogOpen}
            imageSrc={imageToCrop}
            onClose={() => {
              setCropDialogOpen(false)
              setImageToCrop(null)
            }}
            onCropComplete={handleCropComplete}
          />
        </>
      )}
    </>
  )
}

export default NewAndEditRostro
